import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import GoogleIcon from "../../../assets/google-icon-logo.svg";

import authStyles from "../AuthWrapper.module.css";
import loginStyles from "./LoginPage.module.css";

import { loginUser } from "../services";
import { showToast } from "../../../utils/toast";
import { setUser, logoutUser } from "../../../redux/modules/userSlice";
import { setTokens, clearTokens } from "../../../utils/tokens";
import { REVOKED_ACCOUNT_MESSAGE } from "../../../utils/authErrors";
import {
  googleSignInAPI,
  verify2FAAPI,
  resend2FAAPI,
} from "../../../api/userApi";
import { useGoogleLogin } from "@react-oauth/google";
import { setRemember } from "../../../utils/refresh";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { CircularProgress } from "@material-ui/core";
import { identifyMixpanelUser, setMixpanelPeopleProfile } from "../../../config/mixpanel";
import { isStagingCreatorOnboardingBypassEnabled } from "../../../config/stagingCreatorOnboarding";
import PublicLayout from "../../../layouts/PublicLayout";

type TwoFAData = {
  userId: string;
  cooldown?: number;
  remember?: boolean;
};

function LoginPage() {
  const seo = useSEO("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemembered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFAData, setTwoFAData] = useState<TwoFAData | null>(null);
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const targetUsername = searchParams.get("username") || (location.state as any)?.username;
  const redirectPath = targetUsername
    ? (targetUsername.startsWith("/") ? targetUsername : `/${targetUsername}`)
    : "/";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const blockRevokedAccount = (user: { revoke?: boolean } | null | undefined) => {
    if (!user?.revoke) return false;
    clearTokens();
    dispatch(logoutUser());
    showToast(REVOKED_ACCOUNT_MESSAGE + ". Contact support.", "error");
    return true;
  };

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      signInHandler();
    }
  };

  const handle2FAKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify2FA();
    }
  };

  useEffect(() => {
    if (!twoFAData) return;
    setSecondsLeft(twoFAData.cooldown ?? 60);
  }, [twoFAData]);

  useEffect(() => {
    if (!twoFAData || secondsLeft <= 0) return;
    const id = setInterval(
      () => setSecondsLeft(s => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [twoFAData, secondsLeft]);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse: { access_token: string }) => {
      try {
        // Get user info from Google using access token
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );
        const userInfo = await userInfoResponse.json();

        // Send credential to backend
        googleSignInAPI({
          google_access_token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
        })
          .then(response => {
            if (blockRevokedAccount(response.data.user)) return;
            setTokens(response.data.tokens);
            dispatch(setUser(response.data.user));
            identifyMixpanelUser(response.data.user?.id);
            setMixpanelPeopleProfile({
              email: response.data.user?.email || userInfo?.email,
              name: response.data.user?.username || userInfo?.name,
            });
            localStorage.setItem("hasRegisteredOrLoggedIn", "true");
            showToast("Login successful!");
            setTimeout(() => {
              history.push(redirectPath);
            }, 1500);
          })
          .catch(err => {
            const errorMessage =
              err?.response?.data?.message || err?.message || "Login API error";
            showToast(errorMessage, "error");
          });
      } catch (error: any) {
        console.error("Google login error:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error while signing in with Google!";
        showToast(errorMessage, "error");
      }
    },
    onError: () => {
      showToast("Error while signing in with Google!", "error");
    },
  });

  const signInHandler = () => {
    setHasAttemptedLogin(true);
    setIsLoggingIn(true);
    loginUser(email, password, remember)
      .then((res: any) => {
        setLoginError(false);
        if (res.twoFA) {
          setIsLoggingIn(false);
          setTwoFAData({
            userId: res.userId,
            cooldown: res.cooldown,
            remember: res.remember ?? remember,
          });
          return;
        }

        if (blockRevokedAccount(res?.user)) {
          setIsLoggingIn(false);
          return;
        }

        const user = res?.user;
        const userTypeStr =
          typeof user?.userType === "string"
            ? user.userType
            : user?.userType?.title || "";
        const isMember =
          userTypeStr === "member" ||
          userTypeStr === "viewer" ||
          user?.userType?._id === "6800a68085ec077c7c266c27";

        const isCreator =
          userTypeStr === "creator" ||
          userTypeStr === "model" ||
          user?.userType?._id === "6800a63485ec077c7c266c25" ||
          user?.contentProviderAgreementAccepted === true;

        const targetOnboardingPath = isCreator ? "/apply-as-creator" : "/member-onboarding-step";

        const userToStore = isMember
          ? {
              ...user,
              userType: {
                _id: "6800a68085ec077c7c266c27",
                title: "viewer",
                description: "Member / Viewer",
              },
            }
          : isCreator
          ? {
              ...user,
              userType: {
                _id: "6800a63485ec077c7c266c25",
                title: "creator",
                description: "Creator",
              },
            }
          : user;

        dispatch(setUser(userToStore));
        setTokens(res?.tokens);
        identifyMixpanelUser(user?.id);
        setMixpanelPeopleProfile({
          email: user?.email || email,
          name: user?.username || null,
        });
        setRemember(remember);
        localStorage.setItem("hasRegisteredOrLoggedIn", "true");
        localStorage.setItem("user", JSON.stringify(userToStore));

        const isEmailDone = user?.emailVerified === true || user?.validated === true;
        const isPhoneDone = user?.phoneVerified === true || Boolean(user?.phoneNumber);
        const isProfileDone = user?.profileCompletion === true || Boolean(user?.aboutMe);

        console.log("[LoginPage:StepRouter] Evaluated user state:", {
          email: user?.email || email,
          emailVerified: user?.emailVerified,
          validated: user?.validated,
          isEmailDone,
          phoneNumber: user?.phoneNumber,
          phoneVerified: user?.phoneVerified,
          isPhoneDone,
          profileCompletion: user?.profileCompletion,
          aboutMe: user?.aboutMe,
          isProfileDone,
          isOnboarding: user?.isOnboarding,
          isCreator,
          targetOnboardingPath,
        });

        // If user is already fully onboarded or has completed all required member/creator steps -> go to timeline or redirect path
        if (user?.isOnboarding === true || (isEmailDone && isPhoneDone && isProfileDone)) {
          console.log(`[LoginPage:StepRouter] User is complete or isOnboarding=true -> navigating to '${redirectPath}'`);
          showToast("User logged in successfully!");
          setTimeout(() => {
            history.push(redirectPath);
          }, 1000);
          return;
        }

        // 1. If emailVerified == false -> Step 1 (Email Verification) with autoSendOtp: true
        if (!isEmailDone) {
          console.log(`[LoginPage:StepRouter] isEmailDone=false -> navigating to ${targetOnboardingPath} Step 1 (Email Verification)`);
          showToast("Please verify your email to continue");
          setTimeout(() => {
            history.push(targetOnboardingPath, {
              step: 1,
              email: user?.email || email,
              autoSendOtp: true,
            });
          }, 600);
          return;
        }

        // 2. If phone is not provided -> Step 2 (Phone Verification)
        if (!isPhoneDone) {
          console.log(`[LoginPage:StepRouter] isPhoneDone=false -> navigating to ${targetOnboardingPath} Step 2 (Phone Verification)`);
          showToast("Please provide your phone number to continue");
          setTimeout(() => {
            history.push(targetOnboardingPath, {
              step: 2,
              email: user?.email || email,
              initialStepState: "SELECT_METHOD",
              phoneNumber: user?.phoneNumber || "",
            });
          }, 600);
          return;
        }

        // 3. If profile details not completed -> Step 3 (Profile Setup)
        if (!isProfileDone) {
          console.log(`[LoginPage:StepRouter] isProfileDone=false -> navigating to ${targetOnboardingPath} Step 3 (Profile Setup)`);
          showToast("Please complete your profile to continue");
          setTimeout(() => {
            history.push(targetOnboardingPath, {
              step: 3,
              email: user?.email || email,
            });
          }, 600);
          return;
        }

        // 4. Default: go to Step 6 (Choose Membership for member / Verification for creator)
        console.log(`[LoginPage:StepRouter] Default -> navigating to ${targetOnboardingPath} Step 6`);
        setTimeout(() => {
          history.push(targetOnboardingPath, {
            step: 6,
            email: user?.email || email,
          });
        }, 600);
      })
      .catch(error => {
        setIsLoggingIn(false);
        setLoginError(true);
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
            error?.response?.message ||
            error?.message ||
            "Login failed";

        if (
          errorMessage === "Please verify your email!" ||
          (typeof error !== "string" &&
            error?.response?.data?.message
              ?.toLowerCase()
              .includes("verify your email"))
        ) {
          showToast("Please verify your email to continue", "error");
          let isStoredCreator = false;
          try {
            const stored = JSON.parse(localStorage.getItem("user") || "{}");
            const role = (stored?.userType?.title || stored?.userType || "")?.toLowerCase();
            if (role === "creator" || role === "model" || stored?.userType?._id === "6800a63485ec077c7c266c25") {
              isStoredCreator = true;
            }
          } catch {}

          const targetPath = isStoredCreator ? "/apply-as-creator" : "/member-onboarding-step";
          setTimeout(() => {
            history.push(targetPath, {
              step: 1,
              email: email.trim(),
              autoSendOtp: true,
            });
          }, 1000);
        } else {
          showToast(errorMessage, "error");
        }
      });
  };

  const handleForgotPassword = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      showToast("Please enter your email address first.", "error");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }
    history.push(`/forgot-password?email=${encodeURIComponent(trimmedEmail)}`);
  };

  const handleVerify2FA = async () => {
    if (!code || code.length !== 6) {
      return showToast("Enter valid 6-digit code", "error");
    }

    try {
      if (!twoFAData) return;
      setIsVerifying2FA(true);
      const res = await verify2FAAPI({
        userId: twoFAData.userId,
        code,
        remember: twoFAData.remember ?? false,
      });

      if (blockRevokedAccount(res.user)) {
        setIsVerifying2FA(false);
        return;
      }

      dispatch(setUser(res.user));
      setTokens(res.tokens);
      identifyMixpanelUser(res?.user?.id);
      setMixpanelPeopleProfile({
        email: res?.user?.email || email,
        name: res?.user?.username || null,
      });
      localStorage.setItem("hasRegisteredOrLoggedIn", "true");
      showToast("2FA verification successful!");
      setTimeout(() => {
        history.push(redirectPath);
      }, 1500);
    } catch (err) {
      setIsVerifying2FA(false);
      const error = err as any;
      showToast(error?.response?.data?.message || "Invalid 2FA code", "error");
    }
  };

  const handleResend = async () => {
    if (!twoFAData || secondsLeft > 0) return;
    try {
      const res = await resend2FAAPI({ userId: twoFAData.userId });
      setSecondsLeft(res?.cooldown ?? 60);
      showToast(res?.message || "Code resent to your email");
    } catch (err) {
      const error = err as any;
      const cooldown = error?.response?.data?.cooldown;
      if (typeof cooldown === "number") {
        setSecondsLeft(cooldown);
      }
      showToast(error?.response?.data?.message || "Resend failed", "error");
    }
  };

  // ── 2FA screen ───────────────────────────────────────────────
  if (twoFAData) {
    return (
      <PublicLayout>
        <StandardPageHelmet seo={seo} />
        <div className={loginStyles.splitLayout}>
          {/* Left image panel */}
          <div className={loginStyles.imagePanel}>
            <img
              src="/images/exclusive-access.png"
              alt="Unlock Exclusive Access"
              className={loginStyles.bannerImage}
            />
          </div>

          {/* Right form panel */}
          <div className={loginStyles.formPanel}>
            <div className={loginStyles.formInner}>
              <h2 className={loginStyles.cardTitle}>Enter 2FA Code</h2>
              <p className={loginStyles.cardSubtitle}>
                Check your email for the 6-digit verification code.
              </p>

              <input
                type="text"
                placeholder="6-digit code"
                maxLength={6}
                value={code}
                onChange={e =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyPress={handle2FAKeyPress}
                onPaste={e => {
                  const text =
                    (e.clipboardData || (window as any).clipboardData).getData(
                      "text"
                    ) || "";
                  const digits = (text.match(/\d/g) || []).join("").slice(0, 6);
                  if (digits.length) {
                    e.preventDefault();
                    setCode(digits);
                  }
                }}
                className={authStyles.inputField}
                autoComplete="one-time-code"
                name="one-time-code"
                id="two-factor-code"
                inputMode="numeric"
                pattern="\d{6}"
                enterKeyHint="done"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
              />

              <button
                className={loginStyles.primaryBtn}
                onClick={handleVerify2FA}
                disabled={isVerifying2FA}
                id="login-verify-2fa-btn"
              >
                {isVerifying2FA ? (
                  <CircularProgress size={22} style={{ color: "#FFFFFF" }} />
                ) : (
                  "Verify"
                )}
              </button>

              <button
                className={loginStyles.secondaryBtn}
                onClick={handleResend}
                disabled={secondsLeft > 0}
                style={{ marginTop: 12, opacity: secondsLeft > 0 ? 0.7 : 1 }}
                id="login-resend-2fa-btn"
              >
                {secondsLeft > 0 ? `Resend code (${secondsLeft}s)` : "Resend code"}
              </button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // ── Main login screen ─────────────────────────────────────────
  return (
    <PublicLayout>
      <StandardPageHelmet seo={seo} />
      <div className={loginStyles.splitLayout}>
        {/* Left: banner image */}
        <div className={loginStyles.imagePanel}>
          <img
            src="/images/exclusive-access.png"
            alt="Unlock Exclusive Access"
            className={loginStyles.bannerImage}
          />
        </div>

        {/* Right: form (no card bg) */}
        <div className={loginStyles.formPanel}>
          <div className={loginStyles.formInner}>
            <h1 className={loginStyles.cardTitle}>Log in</h1>
            {/* <p className={loginStyles.cardSubtitle}>
              Welcome back — unlock exclusive access.
            </p> */}

            {/* Email */}
            <div className={loginStyles.fieldGroup}>
              <label htmlFor="login-email" className={loginStyles.fieldLabel}>
                Email
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (hasAttemptedLogin) setLoginError(false);
                }}
                onKeyPress={handleKeyPress}
                className={`${authStyles.inputField} ${loginStyles.inputField} ${hasAttemptedLogin && loginError ? authStyles.error : ""
                  }`}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className={loginStyles.fieldGroup}>
              <label htmlFor="login-password" className={loginStyles.fieldLabel}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    if (hasAttemptedLogin) setLoginError(false);
                  }}
                  onKeyPress={handleKeyPress}
                  className={`${authStyles.inputField} ${loginStyles.inputField} ${hasAttemptedLogin && loginError ? authStyles.error : ""
                    }`}
                  style={{ paddingRight: "50px" }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={loginStyles.visibilityBtn}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  id="login-toggle-password"
                >
                  {showPassword ? (
                    <Visibility style={{ fontSize: "20px" }} />
                  ) : (
                    <VisibilityOff style={{ fontSize: "20px" }} />
                  )}
                </button>
              </div>
            </div>

            {/* Remember + Forgot */}
            <div className={loginStyles.rememberRow}>
              <label className={loginStyles.rememberLabel} htmlFor="login-remember">
                <input
                  type="checkbox"
                  id="login-remember"
                  checked={remember}
                  onChange={e => setRemembered(e.target.checked)}
                  className={loginStyles.rememberCheckbox}
                />
                Remember for 30 days
              </label>
              <button
                onClick={handleForgotPassword}
                className={loginStyles.forgotBtn}
                id="login-forgot-password"
              >
                Forgot password ?
              </button>
            </div>

            {/* Sign In */}
            <button
              className={loginStyles.primaryBtn}
              onClick={signInHandler}
              disabled={isLoggingIn}
              id="login-sign-in-btn"
            >
              {isLoggingIn ? (
                <CircularProgress size={22} style={{ color: "#FFFFFF" }} />
              ) : (
                "Sign In"
              )}
            </button>

            {/* Divider */}
            <div className={loginStyles.divider}>
              <span className={loginStyles.dividerLine} />
              <span className={loginStyles.dividerText}>or continue with</span>
              <span className={loginStyles.dividerLine} />
            </div>

            {/* Google */}
            <button
              className={loginStyles.googleBtn}
              onClick={() => googleLogin()}
              id="login-google-btn"
            >
              <img src={GoogleIcon} alt="" aria-hidden="true" className={loginStyles.googleIcon} />
              Sign In With Google
            </button>

            {/* Sign up link */}
            <p className={loginStyles.switchText}>
              Don't have an account?{" "}
              <button
                type="button"
                className={loginStyles.linkBtn}
                onClick={() =>
                  history.push(
                    targetUsername
                      ? `/member-onboarding-step?username=${encodeURIComponent(targetUsername)}`
                      : "/member-onboarding-step"
                  )
                }
                id="login-go-register"
              >
                Sign up
              </button>
            </p>

            {/* Legal */}
            <p className={loginStyles.legalText}>
              By signing in, you agree to our Terms of Service and Privacy Policy, and confirm that you are at least 18 years of age.

              {/* <a href="/terms-of-service" className={loginStyles.legalLink}>
                
              </a>
            
              <a href="/privacy-policy" className={loginStyles.legalLink}>
               
              </a> */}

            </p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default LoginPage;
